import React, { useCallback } from 'react';
import type { UnifiedCitation } from '../../types/citation';
import { toDocumentViewRequest } from '../../types/citation';
import { useDocumentViewer } from '../../contexts/DocumentViewerContext';
import { CitationSuperscript, dotColourIndex } from './CitationSuperscript';
import { RedactionText } from '../redaction/RedactionText';

/**
 * Map dot colour index → matching dashed-underline decoration classes.
 * Keeps the underline colour consistent with the superscript dot.
 */
const UNDERLINE_PALETTE = [
  'decoration-cyan-400/50    hover:decoration-cyan-500/80    dark:decoration-cyan-400/40    dark:hover:decoration-cyan-400/70',
  'decoration-emerald-400/50 hover:decoration-emerald-500/80 dark:decoration-emerald-400/40 dark:hover:decoration-emerald-400/70',
  'decoration-amber-400/50   hover:decoration-amber-500/80   dark:decoration-amber-400/40   dark:hover:decoration-amber-400/70',
  'decoration-rose-400/50    hover:decoration-rose-500/80    dark:decoration-rose-400/40    dark:hover:decoration-rose-400/70',
  'decoration-violet-400/50  hover:decoration-violet-500/80  dark:decoration-violet-400/40  dark:hover:decoration-violet-400/70',
  'decoration-cyan-400/50    hover:decoration-cyan-500/80    dark:decoration-cyan-400/40    dark:hover:decoration-cyan-400/70',
  'decoration-orange-400/50  hover:decoration-orange-500/80  dark:decoration-orange-400/40  dark:hover:decoration-orange-400/70',
  'decoration-pink-400/50    hover:decoration-pink-500/80    dark:decoration-pink-400/40    dark:hover:decoration-pink-400/70',
];

const GROUP_UNDERLINE_PALETTE = [
  'decoration-cyan-400/50    group-hover/cite:decoration-cyan-500/80    dark:decoration-cyan-400/40    dark:group-hover/cite:decoration-cyan-400/70',
  'decoration-emerald-400/50 group-hover/cite:decoration-emerald-500/80 dark:decoration-emerald-400/40 dark:group-hover/cite:decoration-emerald-400/70',
  'decoration-amber-400/50   group-hover/cite:decoration-amber-500/80   dark:decoration-amber-400/40   dark:group-hover/cite:decoration-amber-400/70',
  'decoration-rose-400/50    group-hover/cite:decoration-rose-500/80    dark:decoration-rose-400/40    dark:group-hover/cite:decoration-rose-400/70',
  'decoration-violet-400/50  group-hover/cite:decoration-violet-500/80  dark:decoration-violet-400/40  dark:group-hover/cite:decoration-violet-400/70',
  'decoration-cyan-400/50    group-hover/cite:decoration-cyan-500/80    dark:decoration-cyan-400/40    dark:group-hover/cite:decoration-cyan-400/70',
  'decoration-orange-400/50  group-hover/cite:decoration-orange-500/80  dark:decoration-orange-400/40  dark:group-hover/cite:decoration-orange-400/70',
  'decoration-pink-400/50    group-hover/cite:decoration-pink-500/80    dark:decoration-pink-400/40    dark:group-hover/cite:decoration-pink-400/70',
];

interface CitationEntry {
  citation: UnifiedCitation;
  number: number;
}

interface CitationHighlightProps {
  /** The cited text segment to underline. */
  text: string;
  /** One or more citations attached to this segment. */
  citations: CitationEntry[];
  /** When true, show the underline but suppress the superscript dot(s). */
  hideDots?: boolean;
  /** When true, use group-hover instead of individual hover (for unified multi-paragraph citations). */
  useGroupHover?: boolean;
}

/**
 * Wraps a cited text segment with a colour-matched dashed underline
 * and appends coloured dot superscripts. Clicking the underlined text
 * opens the first citation's document in the side panel.
 */
export const CitationHighlight: React.FC<CitationHighlightProps> = ({ text, citations, hideDots, useGroupHover }) => {
  const { openDocument } = useDocumentViewer();

  const handleTextClick = useCallback(() => {
    if (citations.length === 0) return;
    const primary = citations[0].citation;
    if (!primary.documentId) return;
    openDocument(toDocumentViewRequest(primary));
  }, [citations, openDocument]);

  if (!text && citations.length === 0) return null;

  // Use the primary (first) citation's colour for the underline
  const colourIdx = citations.length > 0 ? dotColourIndex(citations[0].citation.documentId) : 0;
  const palette = useGroupHover ? GROUP_UNDERLINE_PALETTE : UNDERLINE_PALETTE;
  const underlineColour = palette[colourIdx % palette.length];
  const bgHover = useGroupHover
    ? 'group-hover/cite:bg-cyan-50/50 dark:group-hover/cite:bg-cyan-900/20'
    : 'hover:bg-cyan-50/50 dark:hover:bg-cyan-900/20';

  return (
    <span
      className={`underline decoration-dashed decoration-1 underline-offset-[3px] cursor-pointer rounded-sm transition-colors ${bgHover} ${underlineColour}`}
      onClick={handleTextClick}
    >
      <RedactionText text={text} />
      {!hideDots && citations.map(({ citation, number }) => (
        <CitationSuperscript key={number} citation={citation} number={number} />
      ))}
    </span>
  );
};
